import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlagCheckered, faSpinner, faInfoCircle } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import NotificationStatsCardCheckbox from './NotificationStatsCardCheckbox';
import RecommendationStatsModal from './RecommendationStatsModal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import type { LongTermRecommendation, RecommendationCreateStats } from '@/models';

interface FinishLongTermRecommendationModalProps {
  isOpen: boolean;
  onClose: () => void;
  longTermTrade: LongTermRecommendation | null;
  onLongTermRecommendationFinished: (longTermTrade: LongTermRecommendation) => void;
}

export default function FinishLongTermRecommendationModal({
  isOpen,
  onClose,
  longTermTrade,
  onLongTermRecommendationFinished
}: FinishLongTermRecommendationModalProps) {
  const [finalPrice, setFinalPrice] = useState<number>(0);
  const [sendCommunity, setSendCommunity] = useState(true);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  // Stats preview state
  const [estimatedStats, setEstimatedStats] = useState<RecommendationCreateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Stats modal state
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [createdStats, setCreatedStats] = useState<RecommendationCreateStats | null>(null);
  const [finishedTrade, setFinishedTrade] = useState<LongTermRecommendation | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!longTermTrade) return;

    // Validation
    if (longTermTrade.state !== 'OPEN') {
      showErrorToast('Nur offene Trades können beendet werden');
      return;
    }

    if (finalPrice <= 0) {
      showErrorToast('Finalpreis muss größer als 0 sein');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        price:        toCurrencyCentValue(finalPrice),
        sendDiscord:  sendCommunity,
        sendTelegram: sendCommunity,
        sendSCN:      sendCommunity,
        sendPush:     sendCommunity,
        sendMails:    sendCommunity,
        comment:      comment.trim() !== '' ? comment.trim() : null,
      };

      const response = await APIRoutes.finishLongTermTrade(longTermTrade.id, requestData, false);
      setFinishedTrade(response.recommendation);
      setCreatedStats(response.stats);
      setShowStatsModal(true);
    } catch (error) {
      handleAPIError(error, 'Fehler beim Beenden des Langzeit Trades');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFinalPrice(0);
      setSendCommunity(true);
      setComment('');
      setShowStatsModal(false);
      setCreatedStats(null);
      setFinishedTrade(null);
      setEstimatedStats(null);
      setStatsError(null);
      onClose();
    }
  };

  const handleStatsModalClose = () => {
    if (finishedTrade !== null) {
      onLongTermRecommendationFinished(finishedTrade);
    }
    showSuccessToast('Langzeit Trade erfolgreich beendet');
    handleClose();
  };

  // Check if form is valid for fetching stats
  const isFormValid = finalPrice > 0 && longTermTrade !== null && longTermTrade.state === 'OPEN';

  // Build request payload for both submit and dryRun
  const buildRequestPayload = () => {
    if (longTermTrade === null) return null;

    return {
      price:        toCurrencyCentValue(finalPrice),
      sendDiscord:  sendCommunity,
      sendTelegram: sendCommunity,
      sendSCN:      sendCommunity,
      sendPush:     sendCommunity,
      sendMails:    sendCommunity,
      comment:      comment.trim() !== '' ? comment.trim() : null,
    };
  };

  // Fetch estimated notification stats in dryRun mode
  const fetchEstimatedStats = async () => {
    if (!isFormValid || longTermTrade === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const requestPayload = buildRequestPayload();
    if (requestPayload === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await APIRoutes.finishLongTermTrade(longTermTrade.id, requestPayload, true);
      setEstimatedStats(response.stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken';
      setStatsError(errorMessage);
      setEstimatedStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Debounced effect to fetch stats when form changes
  useEffect(() => {
    if (!isFormValid) {
      setEstimatedStats(null);
      setStatsError(null);
      return;
    }

    setStatsLoading(true);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEstimatedStats();
    }, 500);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [finalPrice, sendCommunity, comment, longTermTrade?.id]);

  // Fetch total user count when modal opens
  useEffect(() => {
    if (isOpen) {
      APIRoutes.listUsers({ limit: 1 }).then(response => {
        setTotalUserCount(response.total);
      }).catch(() => {
        setTotalUserCount(null);
      });
    }
  }, [isOpen]);

  // Fetch current symbol price when modal opens
  useEffect(() => {
    if (isOpen && longTermTrade !== null && finalPrice === 0) {
      APIRoutes.listSymbols({ ticker: longTermTrade.symbolTicker, limit: 1 })
        .then(response => {
          const exactMatch = response.symbols.find(s => s.ticker === longTermTrade.symbolTicker);
          if (exactMatch?.currentData?.price !== null && exactMatch?.currentData?.price !== undefined) {
            setFinalPrice(exactMatch.currentData.price);
          }
        })
        .catch(() => {
          // Silently ignore errors - user can still enter price manually
        });
    }
  }, [isOpen, longTermTrade?.symbolTicker]);

  if (!longTermTrade) return null;

  return (
    <>
    <Modal
      isOpen={isOpen && !showStatsModal}
      onClose={handleClose}
      title="Langzeit Trade beenden"
      icon={<FontAwesomeIcon icon={faFlagCheckered} className="w-5 h-5 text-green-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0"
            />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Trade beenden</p>
              <p>
                Das Enddatum und die finale Anpassung werden automatisch erstellt.
                Der Finalpreis wird verwendet, um ein Closing-Adjustment mit negativem Delta zu erstellen,
                das die Position auf 0% setzt.
              </p>
            </div>
          </div>
        </div>

        {/* Trade Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Symbol:</span>
            <span className="font-medium text-gray-900 dark:text-white">{longTermTrade.symbolTicker}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Typ:</span>
            <span className={`font-medium ${
              longTermTrade.type === 'LONG' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {longTermTrade.type}
            </span>
          </div>
        </div>

        {/* Final Price */}
        <div>
          <label htmlFor="finalPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Finalpreis  *
          </label>
          <input
            type="number"
            id="finalPrice"
            value={finalPrice || ''}
            onChange={(e) => setFinalPrice(parseFloat(e.target.value) || 0)}
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="100.00"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Der Preis, zu dem die Position geschlossen wird
          </p>
        </div>

        {/* Community Notification */}
        <NotificationStatsCardCheckbox
          checked={sendCommunity}
          onChange={setSendCommunity}
          disabled={loading}
          colorTheme="green"
          estimatedStats={estimatedStats}
          statsLoading={statsLoading}
          statsError={statsError}
          totalUserCount={totalUserCount}
        />

        {/* Comment */}
        {sendCommunity && (
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kommentar
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Optional: Zusätzlicher Kommentar für die Benachrichtigung"
              disabled={loading}
            />
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || finalPrice <= 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Beende Trade...
              </>
            ) : (
              'Trade beenden'
            )}
          </button>
        </div>
      </form>
    </Modal>

    {createdStats !== null && (
      <RecommendationStatsModal
        isOpen={showStatsModal}
        onClose={handleStatsModalClose}
        stats={createdStats}
        recommendationType="longterm"
        text={`Langzeit-Empfehlung erfolgreich beendet`}
      />
    )}
    </>
  );
}
